<template>
  <div>
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div
      :class="[
        'owc-highlight-slider',
        { 'owc-highlight-slider--dark': isDarkMode },
        { 'owc-highlight-slider--isEditMode': isEditMode },
      ]"
    >
      <div class="owc-highlight-slider__slides">
        <SlotContainer refName="slot-slideImage">
          <slot></slot>
        </SlotContainer>
      </div>
      <div class="owc-highlight-slider__container">
        <wb-highlight-slider
          class="owc-highlight-slider__bar wb-grid-col-mq1-10 wb-grid-col-mq3-8 wb-grid-col-mq4-6"
          :steps="slideSteps"
          @wbchange="changeImages"
          :label="label"
          :theme="swapTheme"
        ></wb-highlight-slider>
      </div>
      <Overlay v-if="!isEditMode"></Overlay>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * HighlightSlider
 * The HighlightSlider which gives a more interactive experience to present car features.
 * The purpose is to present motion content without using videos or motion clips,
 * where the user can drag the slider and see the movement created by a set of images.
 * ValidationBox is available for this component using the slot-name 'validation-box'
 * The component inherits its parent darkmode property.
 */
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { CLASSES, WB_SLIDER_THEME_VALUES } from './HighlightSlider.config';
import { HighlightSliderComponent } from './Highlight';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import Overlay from '@owc-packages/Overlay/src/Overlay.vue';
import { HIGHLIGHT_TRACKING_EVENT_NAME } from './HighlightUtils';

export default (ComponentInitializeDispatcher as HighlightSliderComponent).extend({
  name: 'owc-highlight',
  components: {
    GenericImage,
    SlotContainer,
    ValidationBoxWrapper,
    Overlay,
  },
  mixins: [OwcDataComponent],
  data() {
    return {
      isDragging: false,
      isOnTransition: false,
      barOffsetWidth: 0,
      sliderBar: this.$refs.sliderBar as HTMLElement,
      sliderButton: this.$refs.sliderButton as HTMLElement,
      slides: [] as HTMLElement[],
      selectedTarget: 0,
      sliderIntervalWidth: 0,
      slidesSlot: null,
    };
  },
  /*
   * Mounted
   *
   * Setup the DOM References, adds the event listeners and changed to the first image
   */
  mounted() {
    this.setupDomReferences();
    // if it is webcomponent mode, we need to listen for the `slotchange` when the component is mounted
    if (this.isBuildWc) {
      this.addEventListeners();
    } else {
      this.setupSlider();
    }
  },
  computed: {
    label() {
      return this.componentData?.label;
    },
    isDarkMode() {
      return Boolean(this.componentData?.darkMode);
    },
    swapTheme() {
      return this.isDarkMode ? WB_SLIDER_THEME_VALUES.darkTheme : WB_SLIDER_THEME_VALUES.lightTheme;
    },
    slideSteps() {
      return this.slides.length - 1;
    },
  },
  methods: {
    changeImages(changeEvent) {
      this.changeImage(changeEvent.detail);

      this.$el?.dispatchEvent(
        new CustomEvent(HIGHLIGHT_TRACKING_EVENT_NAME, {
          detail: changeEvent.detail,
          composed: true,
          bubbles: true,
        }),
      );
    },
    /**
     * setupSlider: Sets to active the first image of the slider and calculates the sliderIntervalWidth
     */
    setupSlider() {
      if (this.slides.length > 0) {
        this.changeImage(0);
      }
    },
    /**
     * changeImage: Changes the class of the active image to the correct one. Furthermore,
     * it removes the active class from the previous image.
     *
     * @param {number} targetIndex The index of the active slide
     */
    changeImage(targetIndex): void {
      this.slides.forEach(slide => {
        slide.classList.remove(CLASSES.highlightSliderSlideActive);
      });
      this.slides[targetIndex].classList.add(CLASSES.highlightSliderSlideActive);
    },
    /**
     * setupDomReferences: Setups and stores all the references needed
     *
     */
    setupDomReferences(): void {
      this.sliderBar = this.$refs.sliderBar as HTMLElement;
      this.sliderButton = this.$refs.sliderButton as HTMLElement;
      // When builded via Webcomponent, we need to get the assigned elements
      // from the slot element inside
      if (this.isBuildWc) {
        this.slidesSlot = this.$el.querySelector(
          // We are receiving 2 slots, the default where the images go
          // and one for ValidationBox, for this case whe want to select
          // the default (the one unnnamed).
          'slot:not([name])',
        ) as HTMLSlotElement;
      } else {
        this.slides = [...this.$el.querySelectorAll(`.${CLASSES.highlightSliderSlide}`)] as HTMLElement[];
      }
    },
    addEventListeners(): void {
      this.slidesSlot?.addEventListener('slotchange', () => {
        this.slides = this.slidesSlot?.assignedElements() as HTMLElement[];
        this.slides = this.slides.filter(el => el.tagName === 'OWC-GENERIC-IMAGE');
        if (!this.isEditMode) {
          this.setupSlider();
        }
      });
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils';

%edgePoints {
  display: block;
  width: 4px;
  height: 4px;

  border: 1px solid var(--wb-black);
  border-radius: 50%;

  content: '';
}

@mixin dotGradient($dotColor) {
  background-image: repeating-linear-gradient(
    to left,
    $dotColor 8px,
    $dotColor 10px,
    transparent 10px,
    transparent 26px
  );
}
//transition placeholder. Will be changed after specs are ready
%transition {
  transition: all 400ms ease-in;
}
.owc-highlight-slider {
  $sliderButtonOuterDimensions: 56px;
  $sliderButtonInnerDimensions: 40px;
  $sliderInnerMargins: 26px;
  $chevronDimensions: 10px;
  $sliderTrackHeight: 2px;

  @include aspect-ratio(4, 3);
  @include breakpoint-from(mq4) {
    @include aspect-ratio(16, 9);
  }
  overflow: hidden;

  isolation: isolate;

  &__slides {
    position: absolute;

    .owc-highlight-slider--isEditMode & {
      position: relative;

      display: flex;
      flex-wrap: wrap;
      justify-content: center;

      padding: var(--wb-spacing-m);
      // Slot wrapper div has no width when slot elements are not configured
      // we force the width here, so the author is able to click them
      > div {
        width: 100%;
      }
    }
  }
  &__slide {
    position: absolute;

    opacity: 0;

    &--isActive {
      opacity: 1;
    }
  }
  &__label {
    @include wb-type-hint;

    margin: 0 8px;

    color: var(--wb-white);

    @include breakpoint-to(mq2) {
      color: var(--wb-grey-20);

      .owc-highlight-slider--dark & {
        color: var(--wb-grey-60);
      }
    }
  }
  &__container {
    display: flex;
    align-items: flex-end;
    justify-content: center;

    @include breakpoint-to(mq3) {
      position: relative;

      padding: var(--wb-spacing-xs) 0;

      background: var(--wb-grey-90);
    }
  }
  &__bar {
    z-index: $z-index-content-lvl1;

    margin-bottom: var(--wb-spacing-xs);

    @include breakpoint-to(mq3) {
      margin: 0;
    }
  }
  // Remove slider bar when editMode is enabled
  &--isEditMode {
    .owc-highlight-slider__slider-bar,
    &:before {
      display: none;
    }
  }
  &--dark {
    background-color: var(--wb-black);
  }
}
</style>
